import {useEffect, useMemo, useRef} from "react";
import {css} from "@panda/css";

import {SystemModal} from "@pg-design/modal-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";
import {OfferType} from "@pg-rp/common-types";

import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {IPropertySortableField} from "../../../property/interfaces/IPropertySortableField";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {AreaFilter} from "../../../real_estate/components/filters/AreaFilter";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {getAmplitudePropertyListItem, IAmplitudePropertyListMainOffer} from "../../../tracking/amplitude/get_amplitude_property_list_item";
import {AmplitudePropertyListView} from "../../../tracking/amplitude/track_amplitude_property_view_item_list";
import {ISortOption} from "../../../types/ISortOption";
import {useHomeMatchPreferencesStorage} from "../../../user/hooks/preferences/use_home_match_preferences_storage";
import {useUserPreferencesFilters} from "../../../user/hooks/use_user_preferences_filters";
import {IOfferStandardDescription} from "../../detail/types/IOfferStandardDescription";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {useAccumulatedResults} from "../../hooks/use_accumulated_results";
import {useOfferModalProperties} from "../../hooks/use_offer_modal_properties";
import {getInvestmentApartmentsAmplitudeRequestKey} from "../../list/helpers/investment_apartments_amplitude_item_list_id/create_investment_apartments_amplitude_item_list_id";
import {getInvestmentApartmentsAmplitudeItemListName} from "../../list/helpers/investment_apartments_amplitude_item_list_id/investment_apartments_amplitude_item_list_name";
import {useInvestmentApartmentsAmplitudeItemListId} from "../../list/helpers/investment_apartments_amplitude_item_list_id/use_investment_apartments_amplitude_item_list_id";
import {offerModalInitialState} from "../../state/offer_modal_initial_state";
import {resetPropertyList, setOfferModalFilters, setOfferModalSort} from "../../state/offer_modals_slice";
import {
    floorChoiceOptions,
    houseStoreysOptions,
    mapArea,
    mapBudget,
    mapFromFilterOptions,
    mapRooms
} from "../../tracking/amplitude/amplitude_listing_filters_tracking_utils";
import {AmplitudeInvestmentListingFiltersTrigger} from "../../tracking/amplitude/get_amplitude_investment_listing_filters_applied_event_properties";
import {hitGTMPropertyViewItemList} from "../../tracking/ecommerce_events/property/hit_gtm_property_view_item_list";
import {
    type FilterCategory,
    getMobileFiltersLabelForTracking,
    trackFilterApplyForCategories,
    trackFilterClearForCategories
} from "../../tracking/hit_offer_modal_filters";
import {IOfferModalName} from "../../types/OfferModalName";
import {IOfferModalOffer} from "../../types/OfferModalOffer";
import {IOfferModalState} from "../../types/OfferModalState";
import {getSortValueBySortObj} from "../../utils/get_sort_value_by_sort_obj";
import {FloorFilter} from "./FloorFilter";
import {OfferModalButtons} from "./OfferModalButtons";
import {OfferModalPropertyTable} from "./OfferModalPropertyTable";
import {OfferModalTilesList} from "./OfferModalTilesList";
import {RoomFilter} from "./RoomFilter";

interface IProps {
    handleFiltersModalVisibility: (value: boolean) => void;
    handleSortModalVisibility: (value: boolean) => void;
    filtersModalOpened: boolean;
    mobileSortModalOpened: boolean;
    modalName: IOfferModalName;
    offerDetails: IOfferModalOffer;
    userPreferenceFilters?: ReturnType<typeof useUserPreferencesFilters>;
}

export function OfferModalList(props: IProps) {
    const {modalName, offerDetails, userPreferenceFilters, filtersModalOpened, handleFiltersModalVisibility, handleSortModalVisibility, mobileSortModalOpened} =
        props;

    const modalListRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const propertyListType = useAppSelector((state) => state.offerModals[modalName].type);
    const sortValue = useAppSelector((state) => getSortValueBySortObj(state.offerModals[modalName].sort));
    const filters = useAppSelector((state) => state.offerModals[modalName].filters);
    const rooms = useAppSelector((state) => state.offerModals[modalName].filters.rooms);
    const page = useAppSelector((state) => state.offerModals[modalName].page);
    const pageSize = useAppSelector((state) => state.offerModals[modalName].pageSize);

    const offerDetailProperties = useOfferModalProperties(modalName, userPreferenceFilters);

    const offerType = offerDetails?.type;

    const isHouseFilter = offerType === OfferType.HOUSE;
    const isFlatFilter = offerType === OfferType.FLAT;

    const shouldShowFloorFilter = isFlatFilter || isHouseFilter;

    const initialValue = isFlatFilter ? filters.floor_choices : filters.house_storeys;

    useEffect(() => {
        dispatch(resetPropertyList({modalName}));
    }, [propertyListType]);

    const gtmSource = useAppSelector((state) => state.offerModals[modalName].gtmSource);

    const trackingMeta = useMemo(
        () => ({
            listName: `Zobacz ${getOfferTypeNamePlural(offerDetails.type)}`,
            listId: `zobacz_${getOfferTypeNamePlural(offerDetails.type)}`,
            source: gtmSource
        }),
        [offerDetails.type, gtmSource]
    );
    useEffect(() => {
        if (modalListRef.current) {
            /**
             * Reset scroll position when offer changes.
             * It may happen that scroll position is kept while switching between offers, and
             * in this scenario, we could've been deep down in property list.
             */
            modalListRef.current.scrollTop = 0;
        }
    }, [offerDetails.id]);

    const {hasBeenAccumulated, accumulatedResults} = useAccumulatedResults({
        results: offerDetailProperties.queryData?.results,
        accumulationTime: 3000,
        isLoading: offerDetailProperties.isLoading
    });

    useEffect(() => {
        if (hasBeenAccumulated && accumulatedResults.length) {
            hitGTMPropertyViewItemList({
                offer: offerDetails,
                properties: accumulatedResults,
                listName: trackingMeta.listName,
                listId: trackingMeta.listId
            });
        }
    }, [hasBeenAccumulated, accumulatedResults]);

    const properties = offerDetailProperties.queryData?.results;
    const isPropertyListResponseReady = Boolean(properties) && !offerDetailProperties.isLoading;
    const amplitudeFilters = useMemo(() => getAmplitudeSeeApartmentsFilters(filters), [filters]);
    const amplitudeFiltersKey = JSON.stringify(amplitudeFilters);
    const amplitudeListView: AmplitudePropertyListView = propertyListType === "table" ? "list" : "floor_plans";

    const amplitudeQueryKey = `${offerDetails.id}|${sortValue ?? ""}|${amplitudeFiltersKey}`;
    const amplitudePageRef = useRef({queryKey: amplitudeQueryKey, page});

    if (amplitudePageRef.current.queryKey !== amplitudeQueryKey) {
        amplitudePageRef.current = {queryKey: amplitudeQueryKey, page};
    } else if (page > amplitudePageRef.current.page) {
        amplitudePageRef.current.page = page;
    }

    const amplitudePage = amplitudePageRef.current.page;

    const amplitudeItemListId = useInvestmentApartmentsAmplitudeItemListId({
        offerId: offerDetails.id,
        page: amplitudePage,
        sort: sortValue,
        filters: amplitudeFilters,
        isResponseReady: isPropertyListResponseReady
    });

    const amplitudeItemListName = getInvestmentApartmentsAmplitudeItemListName({offerType: offerDetails.type});

    const amplitudeItemListContext = useMemo(
        () => (amplitudeItemListId ? {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName} : null),
        [amplitudeItemListId, amplitudeItemListName]
    );

    const trackedFiltersAppliedRef = useRef<{offerId: number; filtersKey: string} | null>(null);

    useEffect(() => {
        if (!isPropertyListResponseReady || !amplitudeItemListId) {
            return;
        }

        const trackedFiltersApplied = trackedFiltersAppliedRef.current;
        const isSameOfferList = trackedFiltersApplied?.offerId === offerDetails.id;

        if (isSameOfferList && trackedFiltersApplied?.filtersKey === amplitudeFiltersKey) {
            return;
        }

        trackedFiltersAppliedRef.current = {offerId: offerDetails.id, filtersKey: amplitudeFiltersKey};

        trackAmplitudeSeeApartmentsListingFiltersApplied({
            filters: amplitudeFilters,
            filterTrigger: isSameOfferList ? "manual" : "automatic",
            itemId: offerDetails.id,
            itemListId: amplitudeItemListId
        });
    }, [amplitudeFilters, amplitudeFiltersKey, amplitudeItemListId, isPropertyListResponseReady, offerDetails.id]);

    const amplitudeRequestKey = getInvestmentApartmentsAmplitudeRequestKey({
        offerId: offerDetails.id,
        page: amplitudePage,
        sort: sortValue,
        filters: amplitudeFilters
    });
    const trackedViewItemListKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isPropertyListResponseReady || !amplitudeItemListId || !properties?.length) {
            return;
        }

        const trackedKey = `${amplitudeRequestKey}|${amplitudeListView}`;

        if (trackedViewItemListKeyRef.current === trackedKey) {
            return;
        }

        trackedViewItemListKeyRef.current = trackedKey;

        trackAmplitudeSeeApartmentsViewItemList({
            properties,
            itemId: offerDetails.id,
            itemListId: amplitudeItemListId,
            itemListName: amplitudeItemListName,
            listView: amplitudeListView,
            indexOffset: (page - 1) * pageSize
        });
    }, [
        amplitudeItemListId,
        amplitudeItemListName,
        amplitudeListView,
        amplitudeRequestKey,
        isPropertyListResponseReady,
        offerDetails.id,
        page,
        pageSize,
        properties
    ]);

    const handlers = {
        saveFilters: (data: IFiltersFormValues) => {
            handleFiltersModalVisibility(false);

            const floorChoicesMap: {[index: number]: boolean} = {
                1: data.floorGround,
                2: data.floorGroundWithGarden,
                3: data.floorOneToFour,
                4: data.floorFivePlus,
                5: data.floorLast,
                6: data.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));

            const newFilters = {
                area: data.area,
                rooms: data.rooms,
                ...(isFlatFilter ? {floor_choices: floorChoices} : {house_storeys: floorChoices})
            };

            dispatch(
                setOfferModalFilters({
                    modalName,
                    filters: newFilters
                })
            );

            if (isMobile) {
                const {categories, options} = getMobileFiltersLabelForTracking(newFilters, offerType);

                if (categories) {
                    trackFilterApplyForCategories({
                        eventName: "listing_offer_modal_filters",
                        filterCategory: categories as FilterCategory,
                        customLabel: options || undefined
                    });
                }
            }
        },
        openFilters: () => {
            handleFiltersModalVisibility(true);
        },
        closeFilters: (data?: IFiltersFormValues) => {
            handleFiltersModalVisibility(false);

            if (!data) return;

            dispatch(setOfferModalFilters({modalName, filters: offerModalInitialState.filters}));

            const floorChoicesMap: {[index: number]: boolean} = {
                1: data.floorGround,
                2: data.floorGroundWithGarden,
                3: data.floorOneToFour,
                4: data.floorFivePlus,
                5: data.floorLast,
                6: data.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));

            const currentFilters = {
                rooms: filters.rooms,
                area: filters.area,
                ...(offerType === OfferType.FLAT ? {floor_choices: floorChoices} : {house_storeys: floorChoices})
            };

            const {categories, options} = getMobileFiltersLabelForTracking(currentFilters, offerType);

            trackFilterClearForCategories({
                eventName: "listing_offer_modal_filters",
                filterCategory: categories as FilterCategory,
                customLabel: options || undefined
            });
        },
        openSort: () => {
            handleSortModalVisibility(true);
        },
        saveSort: (newValue: SortOptionsValues) => {
            if (isMobile) {
                handleSortModalVisibility(false);
            }
            const newSort = getSortObjByValue(newValue);
            dispatch(setOfferModalSort({modalName, sort: newSort}));
        },
        clearSort: () => {
            const newSort = getSortObjByValue(SortOptionsValues.LOWEST_PRICE);
            dispatch(setOfferModalSort({modalName, sort: newSort}));
            handleSortModalVisibility(false);
        },
        closeSort: () => {
            handleSortModalVisibility(false);
        }
    };

    const intersectionId = isMobile ? "offer-modal-layout" : "offer-modal-list";

    return (
        <div className={modalList}>
            <SystemModal.Content className={resetPaddingOnDesktop}>
                <div className={topBar}>
                    <div className={topBarRowA} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.PROPERTY_LIST_FILTERS_BAR}>
                        <RoomFilter
                            className={barFilter}
                            initialValue={rooms}
                            trackingParams={{eventName: "listing_offer_modal_filters"}}
                            onConfirm={(value) => {
                                if (offerDetails.region.country === Country.POLAND) {
                                    putPreferences({rooms: value});
                                }

                                hitUserSegmentRooms(value);

                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {...filters, rooms: value}
                                    })
                                );
                            }}
                            handleClear={() => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            rooms: {lower: "", upper: ""}
                                        }
                                    })
                                );
                            }}
                        />

                        <AreaFilter
                            className={barFilter}
                            initialValue={filters.area}
                            trackingParams={{eventName: "listing_offer_modal_filters"}}
                            onConfirm={(value) => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            area: value
                                        }
                                    })
                                );
                            }}
                            handleClear={() => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            area: {lower: "", upper: ""}
                                        }
                                    })
                                );
                            }}
                        />

                        {shouldShowFloorFilter && (
                            <FloorFilter
                                className={barFilter}
                                id="offer-modal-floors"
                                initialValue={initialValue}
                                offerType={offerType}
                                trackingParams={{eventName: "listing_offer_modal_filters"}}
                                onConfirm={(value) => {
                                    dispatch(
                                        setOfferModalFilters({
                                            modalName,
                                            filters: {
                                                ...filters,
                                                [isFlatFilter ? "floor_choices" : "house_storeys"]: value
                                            }
                                        })
                                    );
                                }}
                                handleClear={() => {
                                    dispatch(
                                        setOfferModalFilters({
                                            modalName,
                                            filters: {
                                                ...filters,
                                                [isFlatFilter ? "floor_choices" : "house_storeys"]: []
                                            }
                                        })
                                    );
                                }}
                            />
                        )}
                    </div>
                    {!isMobile && <OfferModalButtons openFilters={handlers.openFilters} openSort={handlers.openSort} modalName={modalName} />}
                </div>
            </SystemModal.Content>
            <div id="offer-modal-list" className={listWrap} ref={modalListRef}>
                {propertyListType === "tiles" && (
                    <OfferModalTilesList
                        modalName={modalName}
                        offer={offerDetails}
                        propertiesQuery={offerDetailProperties}
                        intersectionContainerId={intersectionId}
                        trackingMeta={trackingMeta}
                        amplitudeItemListContext={amplitudeItemListContext}
                    />
                )}
                {propertyListType === "table" && (
                    <OfferModalPropertyTable
                        modalName={modalName}
                        propertiesQuery={offerDetailProperties}
                        offer={offerDetails}
                        intersectionContainerId={intersectionId}
                        trackingMeta={trackingMeta}
                        amplitudeItemListContext={amplitudeItemListContext}
                    />
                )}
            </div>
            <PropertyListFiltersModal
                offerType={offerType}
                filters={filters}
                isOpen={filtersModalOpened}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />
            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={mobileSortModalOpened}
                defaultValue={sortValue || SortOptionsValues.LOWEST_PRICE}
            />
        </div>
    );
}

const SEE_APARTMENTS_AMPLITUDE_LOCATION = "rp_see_apartments";

type IAmplitudeSeeApartmentsFilters = {
    property_type: null;
    budget: string | null;
    rooms: string | null;
    area: string | null;
    floor: string | null;
    completion_date: null;
    house_type: null;
    house_additional_areas: null;
    house_storeys: null;
};

const getAmplitudeSeeApartmentsFilters = (filters: IOfferModalState["filters"]): IAmplitudeSeeApartmentsFilters => {
    const floor = filters.floor_choices?.length
        ? mapFromFilterOptions(filters.floor_choices.map(String), floorChoiceOptions)
        : mapFromFilterOptions((filters.house_storeys ?? []).map(String), houseStoreysOptions);

    return {
        property_type: null,
        budget: (filters.price ? mapBudget(filters.price) : undefined) ?? null,
        rooms: mapRooms(filters.rooms) ?? null,
        area: mapArea(filters.area) ?? null,
        floor: floor ?? null,
        completion_date: null,
        house_type: null,
        house_additional_areas: null,
        house_storeys: null
    };
};

interface ITrackAmplitudeSeeApartmentsListingFiltersAppliedParams {
    filters: IAmplitudeSeeApartmentsFilters;
    filterTrigger: AmplitudeInvestmentListingFiltersTrigger;
    itemId: number;
    itemListId: string;
}

const trackAmplitudeSeeApartmentsListingFiltersApplied = (params: ITrackAmplitudeSeeApartmentsListingFiltersAppliedParams) => {
    const {filters, filterTrigger, itemId, itemListId} = params;

    trackAmplitudeEvent("listing_filters_applied", {
        filter_trigger: filterTrigger,
        location: SEE_APARTMENTS_AMPLITUDE_LOCATION,
        item_id: itemId,
        ...filters,
        item_list_id: itemListId
    });
};

const emptyAmplitudeStandardDescription: Partial<IOfferStandardDescription> = {heating: null, types: []};

const seeApartmentsAmplitudeMainOffer: IAmplitudePropertyListMainOffer = {
    facilities: null,
    parking_places: [],
    standard_description: emptyAmplitudeStandardDescription as IOfferStandardDescription
};

interface ITrackAmplitudeSeeApartmentsViewItemListParams {
    properties: IPropertyListProperty[];
    itemId: number;
    itemListId: string;
    itemListName: string;
    listView: AmplitudePropertyListView;
    indexOffset: number;
}

const trackAmplitudeSeeApartmentsViewItemList = (params: ITrackAmplitudeSeeApartmentsViewItemListParams) => {
    const {properties, itemId, itemListId, itemListName, listView, indexOffset} = params;

    trackAmplitudeEvent("view_item_list", {
        item_id: itemId,
        item_list_id: itemListId,
        item_list_name: itemListName,
        location: SEE_APARTMENTS_AMPLITUDE_LOCATION,
        list_view: listView,
        products: properties.map((property, index) =>
            getAmplitudePropertyListItem({property, mainOffer: seeApartmentsAmplitudeMainOffer, index: indexOffset + index})
        )
    });
};

const getSortObjByValue = (value: number): ISortOption<IPropertySortableField> | null => {
    const option = propertyListSortOptions.find((option) => option.value === value);
    if (!option) {
        return null;
    }

    const isDesc = option.slug.includes("-");
    if (isDesc) {
        return {
            fieldName: option.slug.slice(1) as IPropertySortableField,
            value: "desc"
        };
    }

    return {
        fieldName: option.slug as IPropertySortableField,
        value: "asc"
    };
};

/*
    Styles
 */
const modalList = css({
    width: "100%",
    position: "relative",
    md: {
        display: "flex",
        flexDirection: "column"
    }
});

const listWrap = css({
    md: {
        overflowY: "auto",
        overflowX: "hidden",
        mt: 0,
        mb: 0,
        ml: "-2",
        mr: "-2",
        pt: 0,
        pb: 0,
        pl: 2,
        pr: 2
    }
});

const topBar = css({
    display: "flex",
    justifyContent: "space-between",
    pt: 2,
    pb: 2,
    md: {
        display: "flex",
        flexDirection: "column",
        rowGap: 3
    }
});

const topBarRowA = css({
    display: "none",
    md: {
        display: "flex",
        flex: "auto",
        columnGap: 2
    }
});

const barFilter = css({
    flex: "1 1 33%",
    minWidth: 0
});

const resetPaddingOnDesktop = css({
    display: "none",
    md: {
        display: "block",
        padding: 0
    }
});
import {useEffect, useMemo, useRef} from "react";
import {css} from "@panda/css";

import {SystemModal} from "@pg-design/modal-module";
import {trackAmplitudeEvent} from "@pg-mono/amplitude";
import {hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {SEARCH_FILTERS_AND_SORT_TEST_ID} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";
import {OfferType} from "@pg-rp/common-types";

import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {IPropertySortableField} from "../../../property/interfaces/IPropertySortableField";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {AreaFilter} from "../../../real_estate/components/filters/AreaFilter";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {getAmplitudePropertyListItem, IAmplitudePropertyListMainOffer} from "../../../tracking/amplitude/get_amplitude_property_list_item";
import {AmplitudePropertyListView} from "../../../tracking/amplitude/track_amplitude_property_view_item_list";
import {ISortOption} from "../../../types/ISortOption";
import {useHomeMatchPreferencesStorage} from "../../../user/hooks/preferences/use_home_match_preferences_storage";
import {useUserPreferencesFilters} from "../../../user/hooks/use_user_preferences_filters";
import {IOfferStandardDescription} from "../../detail/types/IOfferStandardDescription";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {getOfferTypeNamePlural} from "../../helpers/OfferType";
import {useAccumulatedResults} from "../../hooks/use_accumulated_results";
import {useOfferModalProperties} from "../../hooks/use_offer_modal_properties";
import {getInvestmentApartmentsAmplitudeRequestKey} from "../../list/helpers/investment_apartments_amplitude_item_list_id/create_investment_apartments_amplitude_item_list_id";
import {getInvestmentApartmentsAmplitudeItemListName} from "../../list/helpers/investment_apartments_amplitude_item_list_id/investment_apartments_amplitude_item_list_name";
import {useInvestmentApartmentsAmplitudeItemListId} from "../../list/helpers/investment_apartments_amplitude_item_list_id/use_investment_apartments_amplitude_item_list_id";
import {offerModalInitialState} from "../../state/offer_modal_initial_state";
import {resetPropertyList, setOfferModalFilters, setOfferModalSort} from "../../state/offer_modals_slice";
import {
    floorChoiceOptions,
    houseStoreysOptions,
    mapArea,
    mapBudget,
    mapFromFilterOptions,
    mapRooms
} from "../../tracking/amplitude/amplitude_listing_filters_tracking_utils";
import {AmplitudeInvestmentListingFiltersTrigger} from "../../tracking/amplitude/get_amplitude_investment_listing_filters_applied_event_properties";
import {hitGTMPropertyViewItemList} from "../../tracking/ecommerce_events/property/hit_gtm_property_view_item_list";
import {
    type FilterCategory,
    getMobileFiltersLabelForTracking,
    trackFilterApplyForCategories,
    trackFilterClearForCategories
} from "../../tracking/hit_offer_modal_filters";
import {IOfferModalName} from "../../types/OfferModalName";
import {IOfferModalOffer} from "../../types/OfferModalOffer";
import {IOfferModalState} from "../../types/OfferModalState";
import {getSortValueBySortObj} from "../../utils/get_sort_value_by_sort_obj";
import {FloorFilter} from "./FloorFilter";
import {OfferModalButtons} from "./OfferModalButtons";
import {OfferModalPropertyTable} from "./OfferModalPropertyTable";
import {OfferModalTilesList} from "./OfferModalTilesList";
import {RoomFilter} from "./RoomFilter";

interface IProps {
    handleFiltersModalVisibility: (value: boolean) => void;
    handleSortModalVisibility: (value: boolean) => void;
    filtersModalOpened: boolean;
    mobileSortModalOpened: boolean;
    modalName: IOfferModalName;
    offerDetails: IOfferModalOffer;
    userPreferenceFilters?: ReturnType<typeof useUserPreferencesFilters>;
}

export function OfferModalList(props: IProps) {
    const {modalName, offerDetails, userPreferenceFilters, filtersModalOpened, handleFiltersModalVisibility, handleSortModalVisibility, mobileSortModalOpened} =
        props;

    const modalListRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const {isMobile} = useUserDevice();

    const {putPreferences} = useHomeMatchPreferencesStorage();

    const propertyListType = useAppSelector((state) => state.offerModals[modalName].type);
    const sortValue = useAppSelector((state) => getSortValueBySortObj(state.offerModals[modalName].sort));
    const filters = useAppSelector((state) => state.offerModals[modalName].filters);
    const rooms = useAppSelector((state) => state.offerModals[modalName].filters.rooms);
    const page = useAppSelector((state) => state.offerModals[modalName].page);
    const pageSize = useAppSelector((state) => state.offerModals[modalName].pageSize);

    const offerDetailProperties = useOfferModalProperties(modalName, userPreferenceFilters);

    const offerType = offerDetails?.type;

    const isHouseFilter = offerType === OfferType.HOUSE;
    const isFlatFilter = offerType === OfferType.FLAT;

    const shouldShowFloorFilter = isFlatFilter || isHouseFilter;

    const initialValue = isFlatFilter ? filters.floor_choices : filters.house_storeys;

    useEffect(() => {
        dispatch(resetPropertyList({modalName}));
    }, [propertyListType]);

    const gtmSource = useAppSelector((state) => state.offerModals[modalName].gtmSource);

    const trackingMeta = useMemo(
        () => ({
            listName: `Zobacz ${getOfferTypeNamePlural(offerDetails.type)}`,
            listId: `zobacz_${getOfferTypeNamePlural(offerDetails.type)}`,
            source: gtmSource
        }),
        [offerDetails.type, gtmSource]
    );
    useEffect(() => {
        if (modalListRef.current) {
            /**
             * Reset scroll position when offer changes.
             * It may happen that scroll position is kept while switching between offers, and
             * in this scenario, we could've been deep down in property list.
             */
            modalListRef.current.scrollTop = 0;
        }
    }, [offerDetails.id]);

    const {hasBeenAccumulated, accumulatedResults} = useAccumulatedResults({
        results: offerDetailProperties.queryData?.results,
        accumulationTime: 3000,
        isLoading: offerDetailProperties.isLoading
    });

    useEffect(() => {
        if (hasBeenAccumulated && accumulatedResults.length) {
            hitGTMPropertyViewItemList({
                offer: offerDetails,
                properties: accumulatedResults,
                listName: trackingMeta.listName,
                listId: trackingMeta.listId
            });
        }
    }, [hasBeenAccumulated, accumulatedResults]);

    const properties = offerDetailProperties.queryData?.results;
    const isPropertyListResponseReady = Boolean(properties) && !offerDetailProperties.isLoading;
    const amplitudeFilters = useMemo(() => getAmplitudeSeeApartmentsFilters(filters), [filters]);
    const amplitudeFiltersKey = JSON.stringify(amplitudeFilters);
    const amplitudeListView: AmplitudePropertyListView = propertyListType === "table" ? "list" : "floor_plans";

    const amplitudeQueryKey = `${offerDetails.id}|${sortValue ?? ""}|${amplitudeFiltersKey}`;
    const amplitudePageRef = useRef({queryKey: amplitudeQueryKey, page});

    if (amplitudePageRef.current.queryKey !== amplitudeQueryKey) {
        amplitudePageRef.current = {queryKey: amplitudeQueryKey, page};
    } else if (page > amplitudePageRef.current.page) {
        amplitudePageRef.current.page = page;
    }

    const amplitudePage = amplitudePageRef.current.page;

    const amplitudeItemListId = useInvestmentApartmentsAmplitudeItemListId({
        offerId: offerDetails.id,
        page: amplitudePage,
        sort: sortValue,
        filters: amplitudeFilters,
        isResponseReady: isPropertyListResponseReady
    });

    const amplitudeItemListName = getInvestmentApartmentsAmplitudeItemListName({offerType: offerDetails.type});

    const amplitudeItemListContext = useMemo(
        () => (amplitudeItemListId ? {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName} : null),
        [amplitudeItemListId, amplitudeItemListName]
    );

    const trackedFiltersAppliedRef = useRef<{offerId: number; filtersKey: string} | null>(null);

    useEffect(() => {
        if (!isPropertyListResponseReady || !amplitudeItemListId) {
            return;
        }

        const trackedFiltersApplied = trackedFiltersAppliedRef.current;
        const isSameOfferList = trackedFiltersApplied?.offerId === offerDetails.id;

        if (isSameOfferList && trackedFiltersApplied?.filtersKey === amplitudeFiltersKey) {
            return;
        }

        trackedFiltersAppliedRef.current = {offerId: offerDetails.id, filtersKey: amplitudeFiltersKey};

        trackAmplitudeSeeApartmentsListingFiltersApplied({
            filters: amplitudeFilters,
            filterTrigger: isSameOfferList ? "manual" : "automatic",
            itemId: offerDetails.id,
            itemListId: amplitudeItemListId
        });
    }, [amplitudeFilters, amplitudeFiltersKey, amplitudeItemListId, isPropertyListResponseReady, offerDetails.id]);

    const amplitudeRequestKey = getInvestmentApartmentsAmplitudeRequestKey({
        offerId: offerDetails.id,
        page: amplitudePage,
        sort: sortValue,
        filters: amplitudeFilters
    });
    const trackedViewItemListKeyRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isPropertyListResponseReady || !amplitudeItemListId || !properties?.length) {
            return;
        }

        const trackedKey = `${amplitudeRequestKey}|${amplitudeListView}`;

        if (trackedViewItemListKeyRef.current === trackedKey) {
            return;
        }

        trackedViewItemListKeyRef.current = trackedKey;

        trackAmplitudeSeeApartmentsViewItemList({
            properties,
            itemId: offerDetails.id,
            itemListId: amplitudeItemListId,
            itemListName: amplitudeItemListName,
            listView: amplitudeListView,
            indexOffset: (page - 1) * pageSize
        });
    }, [
        amplitudeItemListId,
        amplitudeItemListName,
        amplitudeListView,
        amplitudeRequestKey,
        isPropertyListResponseReady,
        offerDetails.id,
        page,
        pageSize,
        properties
    ]);

    const handlers = {
        saveFilters: (data: IFiltersFormValues) => {
            handleFiltersModalVisibility(false);

            const floorChoicesMap: {[index: number]: boolean} = {
                1: data.floorGround,
                2: data.floorGroundWithGarden,
                3: data.floorOneToFour,
                4: data.floorFivePlus,
                5: data.floorLast,
                6: data.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));

            const newFilters = {
                area: data.area,
                rooms: data.rooms,
                ...(isFlatFilter ? {floor_choices: floorChoices} : {house_storeys: floorChoices})
            };

            dispatch(
                setOfferModalFilters({
                    modalName,
                    filters: newFilters
                })
            );

            if (isMobile) {
                const {categories, options} = getMobileFiltersLabelForTracking(newFilters, offerType);

                if (categories) {
                    trackFilterApplyForCategories({
                        eventName: "listing_offer_modal_filters",
                        filterCategory: categories as FilterCategory,
                        customLabel: options || undefined
                    });
                }
            }
        },
        openFilters: () => {
            handleFiltersModalVisibility(true);
        },
        closeFilters: (data?: IFiltersFormValues) => {
            handleFiltersModalVisibility(false);

            if (!data) return;

            dispatch(setOfferModalFilters({modalName, filters: offerModalInitialState.filters}));

            const floorChoicesMap: {[index: number]: boolean} = {
                1: data.floorGround,
                2: data.floorGroundWithGarden,
                3: data.floorOneToFour,
                4: data.floorFivePlus,
                5: data.floorLast,
                6: data.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));

            const currentFilters = {
                rooms: filters.rooms,
                area: filters.area,
                ...(offerType === OfferType.FLAT ? {floor_choices: floorChoices} : {house_storeys: floorChoices})
            };

            const {categories, options} = getMobileFiltersLabelForTracking(currentFilters, offerType);

            trackFilterClearForCategories({
                eventName: "listing_offer_modal_filters",
                filterCategory: categories as FilterCategory,
                customLabel: options || undefined
            });
        },
        openSort: () => {
            handleSortModalVisibility(true);
        },
        saveSort: (newValue: SortOptionsValues) => {
            if (isMobile) {
                handleSortModalVisibility(false);
            }
            const newSort = getSortObjByValue(newValue);
            dispatch(setOfferModalSort({modalName, sort: newSort}));
        },
        clearSort: () => {
            const newSort = getSortObjByValue(SortOptionsValues.LOWEST_PRICE);
            dispatch(setOfferModalSort({modalName, sort: newSort}));
            handleSortModalVisibility(false);
        },
        closeSort: () => {
            handleSortModalVisibility(false);
        }
    };

    const intersectionId = isMobile ? "offer-modal-layout" : "offer-modal-list";

    return (
        <div className={modalList}>
            <SystemModal.Content className={resetPaddingOnDesktop}>
                <div className={topBar}>
                    <div className={topBarRowA} data-testid={SEARCH_FILTERS_AND_SORT_TEST_ID.PROPERTY_LIST_FILTERS_BAR}>
                        <RoomFilter
                            className={barFilter}
                            initialValue={rooms}
                            trackingParams={{eventName: "listing_offer_modal_filters"}}
                            onConfirm={(value) => {
                                if (offerDetails.region.country === Country.POLAND) {
                                    putPreferences({rooms: value});
                                }

                                hitUserSegmentRooms(value);

                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {...filters, rooms: value}
                                    })
                                );
                            }}
                            handleClear={() => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            rooms: {lower: "", upper: ""}
                                        }
                                    })
                                );
                            }}
                        />

                        <AreaFilter
                            className={barFilter}
                            initialValue={filters.area}
                            trackingParams={{eventName: "listing_offer_modal_filters"}}
                            onConfirm={(value) => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            area: value
                                        }
                                    })
                                );
                            }}
                            handleClear={() => {
                                dispatch(
                                    setOfferModalFilters({
                                        modalName,
                                        filters: {
                                            ...filters,
                                            area: {lower: "", upper: ""}
                                        }
                                    })
                                );
                            }}
                        />

                        {shouldShowFloorFilter && (
                            <FloorFilter
                                className={barFilter}
                                id="offer-modal-floors"
                                initialValue={initialValue}
                                offerType={offerType}
                                trackingParams={{eventName: "listing_offer_modal_filters"}}
                                onConfirm={(value) => {
                                    dispatch(
                                        setOfferModalFilters({
                                            modalName,
                                            filters: {
                                                ...filters,
                                                [isFlatFilter ? "floor_choices" : "house_storeys"]: value
                                            }
                                        })
                                    );
                                }}
                                handleClear={() => {
                                    dispatch(
                                        setOfferModalFilters({
                                            modalName,
                                            filters: {
                                                ...filters,
                                                [isFlatFilter ? "floor_choices" : "house_storeys"]: []
                                            }
                                        })
                                    );
                                }}
                            />
                        )}
                    </div>
                    {!isMobile && <OfferModalButtons openFilters={handlers.openFilters} openSort={handlers.openSort} modalName={modalName} />}
                </div>
            </SystemModal.Content>
            <div id="offer-modal-list" className={listWrap} ref={modalListRef}>
                {propertyListType === "tiles" && (
                    <OfferModalTilesList
                        modalName={modalName}
                        offer={offerDetails}
                        propertiesQuery={offerDetailProperties}
                        intersectionContainerId={intersectionId}
                        trackingMeta={trackingMeta}
                        amplitudeItemListContext={amplitudeItemListContext}
                    />
                )}
                {propertyListType === "table" && (
                    <OfferModalPropertyTable
                        modalName={modalName}
                        propertiesQuery={offerDetailProperties}
                        offer={offerDetails}
                        intersectionContainerId={intersectionId}
                        trackingMeta={trackingMeta}
                        amplitudeItemListContext={amplitudeItemListContext}
                    />
                )}
            </div>
            <PropertyListFiltersModal
                offerType={offerType}
                filters={filters}
                isOpen={filtersModalOpened}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />
            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={mobileSortModalOpened}
                defaultValue={sortValue || SortOptionsValues.LOWEST_PRICE}
            />
        </div>
    );
}

const SEE_APARTMENTS_AMPLITUDE_LOCATION = "rp_see_apartments";

type IAmplitudeSeeApartmentsFilters = {
    property_type: null;
    budget: string | null;
    rooms: string | null;
    area: string | null;
    floor: string | null;
    completion_date: null;
    house_type: null;
    house_additional_areas: null;
    house_storeys: null;
};

const getAmplitudeSeeApartmentsFilters = (filters: IOfferModalState["filters"]): IAmplitudeSeeApartmentsFilters => {
    const floor = filters.floor_choices?.length
        ? mapFromFilterOptions(filters.floor_choices.map(String), floorChoiceOptions)
        : mapFromFilterOptions((filters.house_storeys ?? []).map(String), houseStoreysOptions);

    return {
        property_type: null,
        budget: (filters.price ? mapBudget(filters.price) : undefined) ?? null,
        rooms: mapRooms(filters.rooms) ?? null,
        area: mapArea(filters.area) ?? null,
        floor: floor ?? null,
        completion_date: null,
        house_type: null,
        house_additional_areas: null,
        house_storeys: null
    };
};

interface ITrackAmplitudeSeeApartmentsListingFiltersAppliedParams {
    filters: IAmplitudeSeeApartmentsFilters;
    filterTrigger: AmplitudeInvestmentListingFiltersTrigger;
    itemId: number;
    itemListId: string;
}

const trackAmplitudeSeeApartmentsListingFiltersApplied = (params: ITrackAmplitudeSeeApartmentsListingFiltersAppliedParams) => {
    const {filters, filterTrigger, itemId, itemListId} = params;

    trackAmplitudeEvent("listing_filters_applied", {
        filter_trigger: filterTrigger,
        location: SEE_APARTMENTS_AMPLITUDE_LOCATION,
        item_id: itemId,
        ...filters,
        item_list_id: itemListId
    });
};

const emptyAmplitudeStandardDescription: Partial<IOfferStandardDescription> = {heating: null, types: []};

const seeApartmentsAmplitudeMainOffer: IAmplitudePropertyListMainOffer = {
    facilities: null,
    parking_places: [],
    standard_description: emptyAmplitudeStandardDescription as IOfferStandardDescription
};

interface ITrackAmplitudeSeeApartmentsViewItemListParams {
    properties: IPropertyListProperty[];
    itemId: number;
    itemListId: string;
    itemListName: string;
    listView: AmplitudePropertyListView;
    indexOffset: number;
}

const trackAmplitudeSeeApartmentsViewItemList = (params: ITrackAmplitudeSeeApartmentsViewItemListParams) => {
    const {properties, itemId, itemListId, itemListName, listView, indexOffset} = params;

    trackAmplitudeEvent("view_item_list", {
        item_id: itemId,
        item_list_id: itemListId,
        item_list_name: itemListName,
        location: SEE_APARTMENTS_AMPLITUDE_LOCATION,
        list_view: listView,
        products: properties.map((property, index) =>
            getAmplitudePropertyListItem({property, mainOffer: seeApartmentsAmplitudeMainOffer, index: indexOffset + index})
        )
    });
};

const getSortObjByValue = (value: number): ISortOption<IPropertySortableField> | null => {
    const option = propertyListSortOptions.find((option) => option.value === value);
    if (!option) {
        return null;
    }

    const isDesc = option.slug.includes("-");
    if (isDesc) {
        return {
            fieldName: option.slug.slice(1) as IPropertySortableField,
            value: "desc"
        };
    }

    return {
        fieldName: option.slug as IPropertySortableField,
        value: "asc"
    };
};

/*
    Styles
 */
const modalList = css({
    width: "100%",
    position: "relative",
    md: {
        display: "flex",
        flexDirection: "column"
    }
});

const listWrap = css({
    md: {
        overflowY: "auto",
        overflowX: "hidden",
        mt: 0,
        mb: 0,
        ml: "-2",
        mr: "-2",
        pt: 0,
        pb: 0,
        pl: 2,
        pr: 2
    }
});

const topBar = css({
    display: "flex",
    justifyContent: "space-between",
    pt: 2,
    pb: 2,
    md: {
        display: "flex",
        flexDirection: "column",
        rowGap: 3
    }
});

const topBarRowA = css({
    display: "none",
    md: {
        display: "flex",
        flex: "auto",
        columnGap: 2
    }
});

const barFilter = css({
    flex: "1 1 33%",
    minWidth: 0
});

const resetPaddingOnDesktop = css({
    display: "none",
    md: {
        display: "block",
        padding: 0
    }
});
